// ══════════════════════════════════════════════════════════════════════════════
// PAGE EMAILS - Style harmonisé avec Liste de tâches
// AbracadaWorld - Cockpit Particulier
// ══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mail, Inbox, Send, Star, Trash2, Archive, RefreshCw, Search,
  ChevronLeft, ChevronRight, X, Reply, Forward, Paperclip,
  Clock, User, AlertCircle, Check, LogOut, Plus, Edit3, Filter, Sparkles, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import RegisterPromptModal from '../../components/RegisterPromptModal';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const getToken = () => {
  const buyerToken = localStorage.getItem('buyer_token');
  if (buyerToken) return buyerToken;
  const clientToken = localStorage.getItem('client_token');
  if (clientToken) return clientToken;
  return localStorage.getItem('authToken');
};

export default function MailsPage({ onAskAbra, onOpenRegisterModal, onBackToHub }) {
  const [googleStatus, setGoogleStatus] = useState({ connected: false, loading: true });
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('Tous');
  // État local de fallback si la prop parent n'est pas fournie
  const [localShowRegisterModal, setLocalShowRegisterModal] = useState(false);
  const isDarkMode = false;
  
  // Détection mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // État pour détecter le clavier mobile
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  
  // Détection du clavier via visualViewport API
  useEffect(() => {
    if (!showComposeModal || !isMobile) {
      setKeyboardHeight(0);
      return;
    }
    
    // Masquer la navbar quand le modal est ouvert
    document.body.classList.add('modal-fullscreen-open');
    
    const viewport = window.visualViewport;
    if (!viewport) return;
    
    const handleViewportResize = () => {
      const heightDiff = window.innerHeight - viewport.height;
      setKeyboardHeight(heightDiff > 50 ? heightDiff : 0);
    };
    
    viewport.addEventListener('resize', handleViewportResize);
    viewport.addEventListener('scroll', handleViewportResize);
    
    return () => {
      document.body.classList.remove('modal-fullscreen-open');
      viewport.removeEventListener('resize', handleViewportResize);
      viewport.removeEventListener('scroll', handleViewportResize);
    };
  }, [showComposeModal, isMobile]);
  
  const [newEmail, setNewEmail] = useState({
    to: '',
    subject: '',
    body: ''
  });

  const filters = ['Tous', 'Non lus', 'Importants', 'Envoyés', 'Archivés'];

  // Vérifier la connexion Google
  const checkGoogleStatus = async () => {
    try {
      const token = getToken();
      if (!token) {
        setGoogleStatus({ connected: false, loading: false });
        return;
      }
      const res = await fetch(`${API_URL}/api/google/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setGoogleStatus({ ...data, loading: false });
        if (data.connected) {
          loadEmails();
        }
      } else {
        setGoogleStatus({ connected: false, loading: false });
      }
    } catch (error) {
      console.error('Error checking Google status:', error);
      setGoogleStatus({ connected: false, loading: false });
    }
  };

  // Connecter Google
  const connectGoogle = async () => {
    try {
      const token = getToken();
      console.log('[MailsPage] connectGoogle called, token:', token ? 'EXISTS' : 'NULL');
      
      if (!token) {
        // Ouvrir le popup d'inscription avec option Google via le parent
        console.log('[MailsPage] Opening register modal via parent');
        if (onOpenRegisterModal) {
          onOpenRegisterModal('connecter ta boîte mail Gmail', true);
        } else {
          // Fallback si le parent ne fournit pas la prop
          setLocalShowRegisterModal(true);
        }
        return;
      }
      
      const res = await fetch(`${API_URL}/api/google/auth/url`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        window.location.href = data.auth_url;
      } else {
        toast.error('Erreur de connexion Google');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur de connexion');
    }
  };

  // Déconnecter Google
  const disconnectGoogle = async () => {
    if (!window.confirm('Déconnecter ton compte Google ?')) return;
    
    try {
      const token = getToken();
      await fetch(`${API_URL}/api/google/disconnect`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      setGoogleStatus({ connected: false, loading: false });
      setEmails([]);
      toast.success('Compte Google déconnecté');
    } catch (error) {
      toast.error('Erreur de déconnexion');
    }
  };

  // Charger les emails
  const loadEmails = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/google/emails?max_results=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setEmails(data.emails || []);
      }
    } catch (error) {
      console.error('Error loading emails:', error);
      toast.error('Erreur de chargement des emails');
    } finally {
      setLoading(false);
    }
  };

  // Envoyer un email
  const sendEmail = async () => {
    if (!newEmail.to || !newEmail.subject) {
      toast.error('Le destinataire et le sujet sont obligatoires');
      return;
    }

    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/google/emails/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newEmail)
      });

      if (res.ok) {
        toast.success('Email envoyé !');
        setShowComposeModal(false);
        setNewEmail({ to: '', subject: '', body: '' });
        loadEmails();
      } else {
        toast.error('Erreur lors de l\'envoi');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  // Répondre à un email
  const replyToEmail = (email) => {
    const fromMatch = email.from.match(/<(.+)>/);
    const replyTo = fromMatch ? fromMatch[1] : email.from;
    
    setNewEmail({
      to: replyTo,
      subject: email.subject.startsWith('Re:') ? email.subject : `Re: ${email.subject}`,
      body: `\n\n--- Message original ---\nDe: ${email.from}\nDate: ${email.date}\n\n${email.snippet}...`
    });
    setShowComposeModal(true);
    setSelectedEmail(null);
  };

  useEffect(() => {
    checkGoogleStatus();
    
    const params = new URLSearchParams(window.location.search);
    if (params.get('google') === 'connected') {
      toast.success('Compte Google connecté !');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 86400000) {
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } else if (diff < 604800000) {
      return date.toLocaleDateString('fr-FR', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    }
  };

  const extractName = (fromString) => {
    const match = fromString.match(/^([^<]+)/);
    return match ? match[1].trim().replace(/"/g, '') : fromString;
  };

  const filteredEmails = emails.filter(email => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      email.subject?.toLowerCase().includes(query) ||
      email.from?.toLowerCase().includes(query) ||
      email.snippet?.toLowerCase().includes(query)
    );
  });

  const unreadCount = emails.filter(e => e.is_unread).length;

  // ════════════════════════════════════════════════════════════════════════════
  // ÉTAT NON CONNECTÉ - Style harmonisé
  // ════════════════════════════════════════════════════════════════════════════
  if (!googleStatus.loading && !googleStatus.connected) {
    return (
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className={isMobile ? 'mb-2' : 'mb-4'}>
          <div className="flex items-center gap-2">
            {/* Flèche retour - Mobile uniquement */}
            {isMobile && onBackToHub && (
              <button onClick={onBackToHub} className="p-1 -ml-1" data-testid="back-to-hub-btn">
                <ArrowLeft size={20} className={isDarkMode ? 'text-white' : 'text-[#1e3a5f]'} strokeWidth={2} />
              </button>
            )}
            <div>
              <h1 className={`${isMobile ? 'text-[16px]' : 'text-xl'} font-bold ${isDarkMode ? 'text-white' : 'text-[#1e3a5f]'}`}>
                Mes emails
              </h1>
              {!isMobile && (
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Lis, classe et retrouve tes emails rapidement
                </p>
              )}
            </div>
          </div>
        </div>

        {/* État non connecté */}
        <div className={`flex-1 rounded-2xl flex items-center justify-center ${
          isDarkMode ? 'bg-slate-800' : 'bg-white border border-gray-100'
        }`}>
          <div className="text-center p-8 max-w-md">
            {/* Badge Beta */}
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-semibold mb-4">
              <span className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-pulse" />
              Version Beta
            </div>
            
            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 ${
              isDarkMode ? 'bg-slate-700' : 'bg-gradient-to-br from-orange-100 to-orange-50'
            }`}>
              <Mail size={28} className="text-orange-500" />
            </div>
            
            <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Connecte ta boîte mail
            </h3>
            <p className={`text-sm mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Synchronise ton compte pour lire et envoyer tes emails depuis ton cockpit.
            </p>
            
            {/* Gmail - Disponible */}
            <button
              onClick={connectGoogle}
              data-testid="connect-gmail-btn"
              className={`w-full py-3 rounded-xl flex items-center justify-center gap-3 transition-colors mb-3 ${
                isDarkMode 
                  ? 'bg-slate-700 hover:bg-slate-600 border border-slate-600' 
                  : 'bg-white hover:bg-gray-50 border-2 border-gray-200'
              }`}
            >
              <img src="https://www.google.com/favicon.ico" alt="Google" className="w-5 h-5" />
              <span className={`font-semibold text-sm ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                Gmail
              </span>
              <span className="ml-auto px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                Disponible
              </span>
            </button>
            
            {/* Microsoft Outlook - Prochainement */}
            <div className={`w-full py-3 rounded-xl flex items-center justify-center gap-3 mb-3 opacity-60 ${
              isDarkMode ? 'bg-slate-700/50 border border-dashed border-slate-600' : 'bg-gray-50 border-2 border-dashed border-gray-200'
            }`}>
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L2 7v10l10 5 10-5V7L12 2z" fill="#0078D4"/>
              </svg>
              <span className={`font-semibold text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Microsoft Outlook
              </span>
              <span className="ml-auto px-2 py-0.5 bg-blue-100 text-blue-600 text-xs font-semibold rounded-full">
                Bientôt
              </span>
            </div>
            
            {/* iCloud Mail - Prochainement */}
            <div className={`w-full py-3 rounded-xl flex items-center justify-center gap-3 opacity-60 ${
              isDarkMode ? 'bg-slate-700/50 border border-dashed border-slate-600' : 'bg-gray-50 border-2 border-dashed border-gray-200'
            }`}>
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                <path d="M19.5 10.5c0-4.14-3.36-7.5-7.5-7.5S4.5 6.36 4.5 10.5c0 .17.01.34.02.5A5.5 5.5 0 006 21.5h12a5.5 5.5 0 001.5-10.8c0-.07 0-.13 0-.2z" fill="#007AFF"/>
              </svg>
              <span className={`font-semibold text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                iCloud Mail
              </span>
              <span className="ml-auto px-2 py-0.5 bg-blue-100 text-blue-600 text-xs font-semibold rounded-full">
                Bientôt
              </span>
            </div>
            
            <p className={`mt-4 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Tes données restent privées et sécurisées.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  // ÉTAT CONNECTÉ - Style harmonisé
  // ════════════════════════════════════════════════════════════════════════════
  return (
    <div className="h-full flex flex-col">
      {/* Header - Style Liste de tâches */}
      <div className={isMobile ? 'mb-2' : 'mb-4'}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {/* Flèche retour - Mobile uniquement */}
            {isMobile && onBackToHub && (
              <button onClick={onBackToHub} className="p-1 -ml-1" data-testid="back-to-hub-btn">
                <ArrowLeft size={20} className={isDarkMode ? 'text-white' : 'text-[#1e3a5f]'} strokeWidth={2} />
              </button>
            )}
            <div>
              <h1 className={`${isMobile ? 'text-[16px]' : 'text-xl'} font-bold ${isDarkMode ? 'text-white' : 'text-[#1e3a5f]'}`}>
                Mes emails
              </h1>
              {!isMobile && (
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {googleStatus.email || 'Lis, classe et retrouve tes emails rapidement'}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={loadEmails}
              disabled={loading}
              className={`p-2 rounded-xl transition-colors ${
                isDarkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-white border border-gray-100 hover:bg-gray-50'
              }`}
            >
              <RefreshCw size={18} className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'} ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={disconnectGoogle}
              className={`p-2 rounded-xl transition-colors ${
                isDarkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-white border border-gray-100 hover:bg-gray-50'
              }`}
              title="Déconnecter Google"
            >
              <LogOut size={18} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
            </button>
            {isMobile ? (
              <button 
                onClick={() => setShowComposeModal(true)}
                data-testid="compose-email-btn"
                className="flex items-center gap-1 px-2.5 py-1.5 bg-[#F97316] text-white rounded-lg font-semibold text-[11px] whitespace-nowrap"
              >
                Nouveau mail
              </button>
            ) : (
              <Button 
                onClick={() => setShowComposeModal(true)}
                data-testid="compose-email-btn"
                className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl gap-2 h-9 px-4 text-sm font-semibold shadow-sm"
              >
                <Plus size={16} />
                Nouvel email
              </Button>
            )}
          </div>
        </div>

        {/* Stats rapides */}
        <div className="flex items-center gap-4 mt-3">
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
            isDarkMode ? 'bg-slate-800' : 'bg-white border border-gray-100'
          }`}>
            <Mail size={14} className="text-orange-500" />
            <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {emails.length} emails
            </span>
          </div>
          {unreadCount > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-orange-50 border border-orange-100">
              <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
              <span className="text-xs font-medium text-orange-700">
                {unreadCount} non lu{unreadCount > 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Recherche + Filtres */}
      <div className="mb-4">
        {/* Barre de recherche */}
        <div className={`flex items-center gap-2 px-2.5 py-1.5 rounded-xl mb-3 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
          <Search size={14} className="text-gray-400 flex-shrink-0" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher dans les emails..."
            className={`flex-1 bg-transparent border-0 outline-none text-xs placeholder-gray-400 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
          />
        </div>

        {/* Filtres - Style Liste de tâches */}
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {filters.map((filter) => (
            <button
              key={filter}
              onClick={() => setActiveFilter(filter)}
              className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                activeFilter === filter
                  ? 'bg-orange-500 text-white shadow-sm'
                  : (isDarkMode 
                      ? 'bg-slate-800 text-gray-300 hover:bg-slate-700' 
                      : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-100')
              }`}
            >
              {filter}
            </button>
          ))}
        </div>
      </div>

      {/* Liste des emails */}
      <div className={`flex-1 rounded-2xl overflow-hidden ${
        isDarkMode ? 'bg-slate-800' : 'bg-white border border-gray-100'
      }`}>
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredEmails.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center p-8">
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 ${
                isDarkMode ? 'bg-slate-700' : 'bg-gray-50'
              }`}>
                <Mail size={28} className={isDarkMode ? 'text-gray-500' : 'text-gray-300'} />
              </div>
              <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Aucun email trouvé
              </h3>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Tes emails apparaîtront ici après synchronisation.
              </p>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 overflow-y-auto h-full">
            {filteredEmails.map((email) => (
              <motion.div
                key={email.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                onClick={() => setSelectedEmail(email)}
                className={`px-4 py-3 cursor-pointer transition-colors ${
                  isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50'
                } ${email.is_unread ? 'border-l-3 border-l-orange-500' : ''}`}
              >
                <div className="flex items-start gap-3">
                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1e3a5f] to-[#2d4a6f] flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-semibold text-white">
                      {extractName(email.from).charAt(0).toUpperCase()}
                    </span>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className={`text-sm truncate ${
                        email.is_unread 
                          ? (isDarkMode ? 'font-bold text-white' : 'font-bold text-gray-900') 
                          : (isDarkMode ? 'text-gray-300' : 'text-gray-700')
                      }`}>
                        {extractName(email.from)}
                      </span>
                      <span className={`text-[11px] flex-shrink-0 ml-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {formatDate(email.date)}
                      </span>
                    </div>
                    <p className={`text-sm truncate ${
                      email.is_unread 
                        ? (isDarkMode ? 'font-semibold text-white' : 'font-semibold text-gray-800') 
                        : (isDarkMode ? 'text-gray-400' : 'text-gray-600')
                    }`}>
                      {email.subject}
                    </p>
                    <p className={`text-xs truncate mt-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {email.snippet}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Modal détail email */}
      <AnimatePresence>
        {selectedEmail && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 md:left-[288px] bg-black/50 z-50"
              onClick={() => setSelectedEmail(null)}
            />
            <motion.div
              initial={{ opacity: 0, y: '100%' }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: '100%' }}
              className={`fixed inset-x-0 bottom-0 top-20 z-50 flex flex-col rounded-t-2xl ${
                isDarkMode ? 'bg-slate-800' : 'bg-white'
              }`}
            >
              {/* Header email */}
              <div className={`p-4 border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                <div className="flex items-start justify-between mb-3">
                  <button 
                    onClick={() => setSelectedEmail(null)}
                    className={`p-2 -ml-2 rounded-full ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}
                  >
                    <ChevronLeft size={24} className={isDarkMode ? 'text-gray-400' : 'text-gray-600'} />
                  </button>
                  <button 
                    onClick={() => replyToEmail(selectedEmail)}
                    className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}
                  >
                    <Reply size={20} className={isDarkMode ? 'text-gray-400' : 'text-gray-600'} />
                  </button>
                </div>
                
                <h2 className={`text-lg font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  {selectedEmail.subject}
                </h2>
                
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1e3a5f] to-[#2d4a6f] flex items-center justify-center">
                    <span className="text-sm font-semibold text-white">
                      {extractName(selectedEmail.from).charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1">
                    <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      {extractName(selectedEmail.from)}
                    </p>
                    <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      {selectedEmail.date}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Corps email */}
              <div className={`flex-1 overflow-y-auto p-4 ${isDarkMode ? 'bg-slate-800' : 'bg-[#faf9f7]'}`}>
                <p className={`whitespace-pre-wrap ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {selectedEmail.snippet}...
                </p>
                <p className={`mt-4 text-sm italic ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  (Aperçu uniquement - Ouvre Gmail pour voir le message complet)
                </p>
              </div>
              
              {/* Actions */}
              <div className={`p-4 border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                <Button
                  onClick={() => replyToEmail(selectedEmail)}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-xl h-11 font-semibold gap-2"
                >
                  <Reply size={18} />
                  Répondre
                </Button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Modal composer email - Version mobile plein écran via Portal */}
      {showComposeModal && isMobile && createPortal(
        <AnimatePresence>
          <motion.div
            key="compose-email-modal"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="fixed inset-0 z-[10002] flex flex-col bg-white"
          >
            {/* Header */}
            <header 
              className="flex items-center gap-2 px-3 border-b border-gray-100"
              style={{ paddingTop: 'max(12px, env(safe-area-inset-top))', paddingBottom: '12px' }}
            >
              <button 
                onClick={() => setShowComposeModal(false)}
                className="p-1 text-gray-500"
              >
                <ArrowLeft size={22} />
              </button>
              <h1 className="text-[15px] font-bold text-gray-900">Nouvel email</h1>
            </header>

              {/* Contenu scrollable */}
              <div className="flex-1 overflow-y-auto p-5">
                <div className="mb-4">
                  <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Destinataire *</label>
                  <input
                    type="email"
                    value={newEmail.to}
                    onChange={(e) => setNewEmail({ ...newEmail, to: e.target.value })}
                    placeholder="destinataire@email.com"
                    className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Sujet *</label>
                  <input
                    type="text"
                    value={newEmail.subject}
                    onChange={(e) => setNewEmail({ ...newEmail, subject: e.target.value })}
                    placeholder="Objet de l'email..."
                    className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Message</label>
                  <textarea
                    value={newEmail.body}
                    onChange={(e) => setNewEmail({ ...newEmail, body: e.target.value })}
                    placeholder="Écris ton message..."
                    rows={6}
                    className="w-full p-4 rounded-xl border text-[15px] resize-none bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"
                  />
                </div>
              </div>

              {/* Footer fixe */}
              <div 
                className="p-4 border-t border-gray-100 bg-white transition-all duration-200"
                style={{ 
                  paddingBottom: keyboardHeight > 0 ? '16px' : 'calc(16px + env(safe-area-inset-bottom, 0px))',
                  marginBottom: keyboardHeight > 0 ? `${keyboardHeight}px` : '0px'
                }}
              >
                <button
                  onClick={() => {
                    if (onAskAbra) {
                      onAskAbra({ to: newEmail.to, subject: newEmail.subject, body: newEmail.body, isNew: true });
                      setShowComposeModal(false);
                    } else {
                      toast.info('Demande à Abra de t\'aider à rédiger cet email !');
                    }
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 mb-3 text-sm font-medium rounded-xl transition-all text-orange-600 bg-orange-50 hover:bg-orange-100 border border-orange-200"
                >
                  <Sparkles size={16} />
                  Créer avec Abracada
                </button>
                
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowComposeModal(false)}
                    className="flex-1 h-12 rounded-xl font-semibold transition-all border border-gray-300 text-[#1e3a5f] hover:bg-gray-100"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={sendEmail}
                    data-testid="send-email-btn"
                    disabled={!newEmail.to || !newEmail.subject}
                    className="flex-1 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold shadow-lg shadow-orange-500/25 transition-all disabled:opacity-50"
                  >
                    Envoyer
                  </button>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>,
        document.body
      )}

      {/* Modal composer email - Version desktop centré */}
      <AnimatePresence>
        {showComposeModal && !isMobile && (
            /* ═══ VERSION DESKTOP ═══ */
            <div className="fixed inset-0 md:left-[288px] z-[10002] flex items-center justify-center p-4 bg-black/60">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl max-h-[90vh] flex flex-col"
              >
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                  <h3 className="text-lg font-semibold text-gray-900">Nouvel email</h3>
                  <button onClick={() => setShowComposeModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                    <X size={20} className="text-gray-500" />
                  </button>
                </div>

                <div className="p-6 space-y-4 overflow-y-auto">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Destinataire *</label>
                    <input
                      type="email"
                      value={newEmail.to}
                      onChange={(e) => setNewEmail({ ...newEmail, to: e.target.value })}
                      placeholder="destinataire@email.com"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Sujet *</label>
                    <input
                      type="text"
                      value={newEmail.subject}
                      onChange={(e) => setNewEmail({ ...newEmail, subject: e.target.value })}
                      placeholder="Objet de l'email..."
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Message</label>
                    <textarea
                      value={newEmail.body}
                      onChange={(e) => setNewEmail({ ...newEmail, body: e.target.value })}
                      placeholder="Écris ton message..."
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
                    />
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowComposeModal(false)}>
                      Annuler
                    </Button>
                    <button
                      type="button"
                      onClick={() => {
                        if (onAskAbra) {
                          onAskAbra({ to: newEmail.to, subject: newEmail.subject, body: newEmail.body, isNew: true });
                          setShowComposeModal(false);
                        } else {
                          toast.info('Demande à Abra de t\'aider à rédiger cet email !');
                        }
                      }}
                      className="px-4 py-2 rounded-lg font-semibold text-white flex items-center gap-2"
                      style={{ background: 'linear-gradient(145deg, #E8955C 0%, #D4784A 100%)' }}
                    >
                      <Sparkles size={16} />
                      Demander à Abra
                    </button>
                    <Button
                      onClick={sendEmail}
                      data-testid="send-email-btn"
                      disabled={!newEmail.to || !newEmail.subject}
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                    >
                      Envoyer
                    </Button>
                  </div>
                </div>
              </motion.div>
            </div>
        )}
      </AnimatePresence>

      {/* Modal d'inscription contextuel fallback (si parent ne gère pas) */}
      {!onOpenRegisterModal && (
        <RegisterPromptModal 
          isOpen={localShowRegisterModal} 
          onClose={() => setLocalShowRegisterModal(false)}
          actionLabel="connecter ta boîte mail Gmail"
          showGoogleOption={true}
        />
      )}
    </div>
  );
}
